import React, { Fragment, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import SegmentIO from 'reporting/SegmentIO';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { colors, fontSize, breakpoints } from 'styles/cp';

const splunkReporter = SplunkReporter.getInstance();
const logger = 'components/Core/Payment/Growth/PlaidExperiment/PlaidErrorScreen';

export type PlaidErrorScreenProps = {
  onTryAgain: () => void;
  onEnterManually: () => void;
  domainId: string;
  recipientEmail: string;
};

const PlaidErrorScreen: React.FC<PlaidErrorScreenProps> = ({
  onTryAgain,
  onEnterManually,
  domainId,
  recipientEmail,
}) => {
  useEffect(() => {
    splunkReporter.contextual({
      logInfo: { logLevel: 'error', logger },
      event: 'pay',
      action: 'render',
      activityInfo: {
        activityObject: 'PlaidErrorScreen',
        plaidAction: 'error_screen_displayed',
        domainId,
      },
    });
    SegmentIO.plaidErrorScreenViewed(domainId, recipientEmail);
  }, []);

  return (
    <Fragment>
      <div className="plaid-error-screen" role="alert" data-testid="plaid-exchange-error">
        <h3 className="plaid-error-screen__heading">
          <FormattedMessage
            id="PLAID_SOMETHING_ISNT_WORKING"
            defaultMessage="Something isn't working"
          />
        </h3>
        <p className="plaid-error-screen__description">
          <FormattedMessage
            id="PLAID_ERROR_DESCRIPTION"
            defaultMessage="Please try again, or enter your info manually."
          />
        </p>
        <div className="plaid-error-screen__actions">
          <button
            className="plaid-error-screen__try-again"
            onClick={onTryAgain}
            aria-label="Try again"
            data-testid="plaid-error-try-again"
          >
            <FormattedMessage id="TRY_AGAIN" defaultMessage="Try again" />
          </button>
          <button
            className="plaid-error-screen__manual-entry"
            onClick={onEnterManually}
            aria-label="Enter account manually"
            data-testid="plaid-error-manual-entry"
          >
            <FormattedMessage
              id="PLAID_ENTER_ACCOUNT_MANUALLY"
              defaultMessage="Enter account manually"
            />
          </button>
        </div>
      </div>

      <style jsx>{`
        .plaid-error-screen {
          padding: 32px 24px;
          text-align: center;
          border-radius: 4px;
        }

        .plaid-error-screen__heading {
          font-family: AvenirNextforINTUIT-Demi;
          font-size: ${fontSize.md2};
          color: ${colors.gray};
          margin: 0 0 4px 0;
        }

        .plaid-error-screen__description {
          font-size: ${fontSize.sm};
          color: ${colors.gray02};
          margin: 0 0 24px 0;
        }

        .plaid-error-screen__actions {
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
          gap: 16px;
        }

        .plaid-error-screen__try-again {
          background: transparent;
          border: none;
          cursor: pointer;
          font-family: AvenirNextforINTUIT-Demi;
          font-size: ${fontSize.sm};
          color: ${colors.green};
          padding: 10px 18px;
          border-radius: 4px;
        }

        .plaid-error-screen__try-again:hover {
          color: ${colors.green01};
        }

        .plaid-error-screen__try-again:focus {
          outline: none;
          box-shadow: 0px 0px 0px 1px white, 0px 0px 0px 4px ${colors.intuit_blue};
        }

        .plaid-error-screen__manual-entry {
          background-color: ${colors.white};
          border: 2px solid ${colors.green};
          cursor: pointer;
          font-family: AvenirNextforINTUIT-Demi;
          font-size: ${fontSize.sm};
          color: ${colors.green};
          padding: 10px 24px;
          min-height: 40px;
          border-radius: 4px;
        }

        .plaid-error-screen__manual-entry:hover {
          background-color: ${colors.white};
          border-color: ${colors.green01};
          color: ${colors.green01};
        }

        .plaid-error-screen__manual-entry:focus {
          outline: none;
          box-shadow: 0px 0px 0px 1px white, 0px 0px 0px 4px ${colors.intuit_blue};
        }

        @media screen and (max-width: ${breakpoints.sm}) {
          .plaid-error-screen {
            padding: 24px 16px;
          }

          .plaid-error-screen__actions {
            flex-direction: column-reverse;
          }

          .plaid-error-screen__manual-entry {
            width: 100%;
          }

          .plaid-error-screen__try-again {
            width: 100%;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default PlaidErrorScreen;
